import { useEffect, useRef, useState } from 'react';

// The video file lives in apps/web/public/brand/ and is served by the ops
// web app at connect.fleetup.in — NOT the marketing site at fleetup.in
// (the marketing site's SPA catch-all happily returns index.html for any
// /brand/* path, which made the previous URL look like a working asset
// while actually serving HTML that <video> can't decode).
const BRAND_VIDEO_URL = 'https://connect.fleetup.in/brand/FleetUp-Upscaled.mp4';

/**
 * Fullscreen FleetUp brand video used as the auth-screen backdrop.
 *
 * iOS Safari quirks handled here:
 *   1. autoplay attribute alone is unreliable — we also call .play()
 *      programmatically on mount (within the same task as user interaction
 *      where possible, but we accept a silent AbortError if the policy says no)
 *   2. cross-origin video sometimes never decodes; the onError handler hides
 *      the broken element and exposes a gradient fallback that matches the
 *      brand colourway, so the screen never looks "flat" if the video fails
 *
 * The video sits on top of a mint→green→dark gradient at the same zIndex.
 * Even when the video loads successfully, its 0.75 opacity lets a hint of
 * the gradient bleed through — which is the intended depth, not a bug.
 */
export function BrandVideoBackdrop() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // iOS Safari sometimes needs an explicit play() call even when autoplay
    // is set. We ignore the AbortError that fires if the user navigates away
    // before play resolves, and the NotAllowedError if autoplay policy
    // blocks us (the backdrop gracefully degrades to the gradient).
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => { /* policy denied autoplay; gradient fallback is fine */ });
    }
  }, []);

  return (
    <>
      {/* Gradient fallback — sits BEHIND the video at the same z-index. Visible
          through the video's 0.75 opacity even when video loads; takes over
          fully if the video errors. Mint-soft top → brand green mid → near-black
          bottom for a calm, on-brand backdrop. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(155deg, #C6DDD0 0%, #1B6A41 55%, #0A2418 100%)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      {videoFailed ? null : (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          /* Do NOT set crossOrigin. Plain <video src=cross-origin> playback
             works without CORS headers; setting crossOrigin="anonymous"
             flips the request into a CORS request that fleetup.in does NOT
             send `Access-Control-Allow-Origin` for, which makes Chrome +
             Safari hard-fail the load. We don't read pixels, so we don't
             need CORS at all. */
          src={BRAND_VIDEO_URL}
          onError={() => setVideoFailed(true)}
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.75,
            filter: 'saturate(1.05) brightness(1.02) blur(4px)',
            transform: 'scale(1.06)',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
}
